// src/pages/cadastros/Cooperadores/CooperadoresPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";

import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import CardListMobile from "../../../templates/mobile/CardListMobile";

import { useSortableTable, SortableHeader } from "../../../hooks/useSortableTable";
import { pode } from "../../../utils/permissoes";

import { db } from "../../../config/firebase";
import { collection, deleteDoc, doc, getDocs, query, where } from "firebase/firestore";

function renderCompetencias(c) {
  if (!c) return "-";
  if (Array.isArray(c)) return c.filter(Boolean).join(", ") || "-";
  return String(c);
}

function renderHorario(ent, sai) {
  const e = (ent || "").trim();
  const s = (sai || "").trim();
  if (!e && !s) return "-";
  if (e && s) return `${e} → ${s}`;
  if (e) return `${e} → -`;
  return `- → ${s}`;
}

export default function CooperadoresPage() {
  const { modoGlobal, empresaAtual } = useEmpresa();
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const isGlobalAccess = !!usuarioLogado?.globalAccess || usuarioLogado?.tipoAcesso === "GLOBAL";

  const canVer = pode(usuarioLogado, "cadastros", "cooperadores", "tabela");
  const canCriar = pode(usuarioLogado, "cadastros", "cooperadores", "form");
  const canEditar = pode(usuarioLogado, "cadastros", "cooperadores", "altera");

  const [cooperadores, setCooperadores] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [campoFiltro, setCampoFiltro] = useState("usuarioNome");
  const [textoFiltro, setTextoFiltro] = useState("");

  const camposFiltro = useMemo(
    () => [
      { value: "usuarioNome", label: "Usuário" },
      { value: "cargo", label: "Cargo" },
      { value: "competencias", label: "Competências" },
      { value: "empresaNome", label: "Empresa" },
    ],
    []
  );

  useEffect(() => {
    async function carregar() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const ref = collection(db, "cooperadores");
        const filtrosBase = [];

        if (usuarioLogado.grupoId) {
          filtrosBase.push(where("grupoId", "==", usuarioLogado.grupoId));
        } else {
          // evita consulta que retorna tudo
          filtrosBase.push(where("usuarioNome", "==", "__nulo__"));
        }

        // Empresa específica (modo normal)
        if (!modoGlobal && empresaAtual) {
          const filtros = [...filtrosBase];
          if (empresaAtual.id) filtros.push(where("empresaId", "==", empresaAtual.id));
          else if (empresaAtual.nome) filtros.push(where("empresaNome", "==", empresaAtual.nome));

          const snap = await getDocs(query(ref, ...filtros));
          const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          lista.sort((a, b) => {
            const ta = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
            const tb = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
            return tb - ta;
          });
          setCooperadores(lista);
          return;
        }

        // Global (sem acesso global real): consulta empresas permitidas
        if (modoGlobal && !isGlobalAccess) {
          const empresas = Array.isArray(usuarioLogado.empresasPermitidas) ? usuarioLogado.empresasPermitidas : [];
          if (empresas.length === 0) {
            setCooperadores([]);
            setErro("Seu usuário não possui acesso global e não possui empresas permitidas para consulta.");
            return;
          }

          const snaps = await Promise.all(
            empresas.map((emp) => {
              const filtros = [...filtrosBase];
              if (emp?.id) filtros.push(where("empresaId", "==", emp.id));
              else if (emp?.nome) filtros.push(where("empresaNome", "==", emp.nome));
              else filtros.push(where("usuarioNome", "==", "__nulo__"));
              return getDocs(query(ref, ...filtros));
            })
          );

          const map = new Map();
          for (const s of snaps) {
            for (const d of s.docs) {
              map.set(d.id, { id: d.id, ...d.data() });
            }
          }
          const lista = Array.from(map.values());
          lista.sort((a, b) => {
            const ta = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
            const tb = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
            return tb - ta;
          });
          setCooperadores(lista);
          return;
        }

        // Global com acesso global real
        const snap = await getDocs(query(ref, ...filtrosBase));
        const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        lista.sort((a, b) => {
          const ta = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const tb = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return tb - ta;
        });
        setCooperadores(lista);

      } catch (e) {
        console.error("Erro ao carregar cooperadores:", e);
        const code = e?.code ? String(e.code) : "";
        const msg = e?.message ? String(e.message) : "";
        setErro(code || msg ? `Erro ao carregar cooperadores: ${code || msg}` : "Erro ao carregar cooperadores.");
        setCooperadores([]);
      } finally {
        setCarregando(false);
      }
    }

    if (canVer) carregar();
    else {
      setCooperadores([]);
      setCarregando(false);
      setErro("Sem permissão para visualizar Cooperadores.");
    }
  }, [usuarioLogado, modoGlobal, empresaAtual, canVer, isGlobalAccess]);

  const cooperadoresFiltrados = useMemo(() => {
    const t = textoFiltro.trim().toLowerCase();
    if (!t) return cooperadores;

    return cooperadores.filter((c) => {
      let base = "";
      if (campoFiltro === "competencias") base = renderCompetencias(c.competencias);
      else base = c?.[campoFiltro] ?? "";
      return String(base).toLowerCase().includes(t);
    });
  }, [cooperadores, campoFiltro, textoFiltro]);

  const { sortedData, requestSort, getSortDirection } = useSortableTable(cooperadoresFiltrados, "usuarioNome");

  async function handleExcluir(id) {
    if (!id) return;
    const ok = window.confirm("Tem certeza que deseja excluir este cooperador? Essa ação não pode ser desfeita.");
    if (!ok) return;

    setCarregando(true);
    setErro("");

    try {
      await deleteDoc(doc(db, "cooperadores", id));
      setCooperadores((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      console.error("Erro ao excluir cooperador:", e);
      setErro("Erro ao excluir cooperador. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  }

  const itemsParaCards = useMemo(
    () =>
      cooperadoresFiltrados.map((c) => ({
        id: c.id,
        nome: c.usuarioNome || "(Sem usuário)",
        subtitulo: c.cargo || "-",
        detalhes: [
          { icone: "🧠", label: "Competências", valor: renderCompetencias(c.competencias) },
          { icone: "⏱️", label: "Horário", valor: renderHorario(c.horarioEntrada, c.horarioSaida) },
          { icone: "🏭", label: "Empresa", valor: c.empresaNome || "-" },
          { icone: "✅", label: "Ativo", valor: c.ativo === false ? "Não" : "Sim" },
        ],
      })),
    [cooperadoresFiltrados]
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Cooperadores"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <CardListMobile
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando cooperadores..."
          titulo="Cooperadores (Manutenção)"
          searchPlaceholder="Buscar cooperador..."
          novoLabel="Novo Cooperador"
          emptyMessage="Nenhum cooperador encontrado"
          onHome={() => navigate("/")}
          onNovo={canCriar ? () => navigate("/cadastros/cooperadores/novo") : undefined}
          onEditar={canEditar ? (id) => navigate(`/cadastros/cooperadores/editar/${id}`) : undefined}
          getSearchText={(item) => `${item.nome} ${item.subtitulo}`}
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo="Cooperadores (Manutenção)" subtitulo="" >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Cooperadores cadastrados</h3>
            <p>Registro de competências e horários da equipe de manutenção.</p>
          </div>

          <div className="pc-table-header-actions">
            {canCriar && (
              <button className="pc-btn-primario" onClick={() => navigate("/cadastros/cooperadores/novo")}>Novo Cooperador</button>
            )}
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campo={campoFiltro}
          onCampoChange={setCampoFiltro}
          texto={textoFiltro}
          onTextoChange={setTextoFiltro}
          placeholder="Filtrar..."
        />

        {erro && <div className="pc-erro" style={{ marginTop: 10 }}>{erro}</div>}

        <div className="pc-table-wrap" style={{ marginTop: 14 }}>
          <table className="pc-table">
            <thead>
              <tr>
                <SortableHeader
                  label="Usuário"
                  sortKey="usuarioNome"
                  onSort={requestSort}
                  direction={getSortDirection("usuarioNome")}
                />
                <SortableHeader
                  label="Cargo"
                  sortKey="cargo"
                  onSort={requestSort}
                  direction={getSortDirection("cargo")}
                />
                <th>Competências</th>
                <th>Horário</th>
                <th>Empresa</th>
                <th>Ativo</th>
                <th style={{ width: 140 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr>
                  <td colSpan={7} style={{ padding: 18, opacity: 0.8 }}>Carregando...</td>
                </tr>
              ) : sortedData.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 18, opacity: 0.8 }}>Nenhum registro.</td>
                </tr>
              ) : (
                sortedData.map((c) => (
                  <tr key={c.id}>
                    <td>{c.usuarioNome || "-"}</td>
                    <td>{c.cargo || "-"}</td>
                    <td style={{ maxWidth: 380, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={renderCompetencias(c.competencias)}>
                      {renderCompetencias(c.competencias)}
                    </td>
                    <td>{renderHorario(c.horarioEntrada, c.horarioSaida)}</td>
                    <td>{c.empresaNome || "-"}</td>
                    <td>{c.ativo === false ? "Não" : "Sim"}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {canEditar && (
                        <button
                          type="button"
                          className="pc-icon-btn"
                          title="Editar cooperador"
                          onClick={() => navigate(`/cadastros/cooperadores/editar/${c.id}`)}
                        >
                          ✏️
                        </button>
                      )}
                      {canEditar && (
                        <button
                          type="button"
                          className="pc-icon-btn"
                          title="Excluir cooperador"
                          onClick={() => handleExcluir(c.id)}
                        >
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </LayoutDesktop>
  );
}
